import { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  Button,
  Card,
  Link,
  Text,
  Icon,
  Popover,
  Badge,
  CardColors,
} from '@/ui'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectStatusForLateJoinerTaxEstimate } from '@/features/taxes/quarterly-tax-estimates/userTaxEstimates.selector'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

import { EstimateStatus } from '@/features/taxes/quarterly-tax-estimates/service'
import useTaxPaymentInfoFlyout from '../TaxPaymentInfoFlyout'
import { splitTaxYearQuarter } from '../../helpers'
import { SafeHarborType, UserTaxEstimate } from '../../userTaxEstimates.slice'
import { WhatIsSafeHarborPopup } from './helpers'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { getUserDocumentById } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { TextColors, TypographyStyles } from '@/styles'

// TODO: Migrate to V2 skipcq: JS-0099
import UpdateQuarterlyTaxEstimateModal from '@/featuresV1/Taxes/QuarterlyTaxEstimates/components/UpdateQuarterlyTaxEstimateModal'

const WhatIsEs1040Popup = () => (
  <Popover>
    <Popover.Trigger asChild>
      <Button
        variant="link"
        className="text-sm text-muted-foreground p-0 h-auto"
      >
        Where did this number come from?
      </Button>
    </Popover.Trigger>
    <Popover.Content className="max-w-sm space-y-2">
      <Text variant={TypographyStyles.H3}>
        Where did this number come from?
      </Text>
      <Text>
        When you first joined Heard, we took you through some questions to
        figure out your quarterly estimate. We asked you to look at last
        year&apos;s 1040 Individual Tax Return and find the 1040-ES payment
        voucher to find the suggested amount.
      </Text>
    </Popover.Content>
  </Popover>
)

export const SafeHarborPaymentCard = ({
  federalTaxEstimate,
}: {
  federalTaxEstimate: UserTaxEstimate
}) => {
  const dispatch = useAppDispatch()
  const [estimateModalOpen, setEstimateModalOpen] = useState(false)

  const { quarter } = splitTaxYearQuarter(federalTaxEstimate?.taxQuarter)

  const openFlyout = useTaxPaymentInfoFlyout({
    estimateId: federalTaxEstimate?.id,
    openQTEModal: () => setEstimateModalOpen(true),
  })

  const status = useReselector(
    selectStatusForLateJoinerTaxEstimate,
    federalTaxEstimate?.id
  )

  const document = useReselector(
    getUserDocumentById,
    federalTaxEstimate?.userDocumentId
  )

  useEffect(() => {
    dispatch(fetchUserDocuments())
  }, [dispatch])

  const conditionalContent = useMemo(() => {
    switch (status) {
      case EstimateStatus.unpaidWithResponse:
        return {
          label: <Badge>No payment</Badge>,
          bottomContent: (
            <>
              <Text className="w-full" color={TextColors.MOSS}>
                Thanks for letting us know you did not make a payment this
                quarter.
              </Text>
              <Button
                onClick={() => setEstimateModalOpen(true)}
                variant="link"
                className="w-full"
              >
                Edit Payment
              </Button>
            </>
          ),
        }
      case EstimateStatus.estimateIsZero:
        return {
          label: <Badge color="orange">Ready for Payment</Badge>,
          bottomContent: (
            <div className="space-y-4">
              <Card className="bg-green-100">
                <Text className="text-accentGreen">
                  Please confirm how much you paid (even if this is $0) using
                  the &quot;Update status&quot; button.
                </Text>
              </Card>
              <Popover>
                <Popover.Trigger asChild>
                  <Button variant="link">Why is my estimate $0?</Button>
                </Popover.Trigger>
                <Popover.Content className="max-w-sm">
                  <Text variant={TypographyStyles.H3}>
                    Why is my estimate $0?
                  </Text>
                  <br />
                  <Text>
                    Since you indicated your tax liability for last year was $0,
                    you are not required to pay quarterly taxes. If that number
                    isn&apos;t right, you can edit your responses and provide
                    the correct tax liability.
                  </Text>
                </Popover.Content>
              </Popover>
              <Text>
                We need this to provide accurate estimates for upcoming quarters
              </Text>
              <Text>
                Need to edit estimate? Click{' '}
                <Link
                  href="/taxes/quarterly/calculate-safe-harbor"
                  variant="secondary"
                >
                  here
                </Link>
                .
              </Text>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="w-full" onClick={openFlyout}>
                  View & Pay
                </Button>
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={() => setEstimateModalOpen(true)}
                >
                  Update status
                </Button>
              </div>
            </div>
          ),
        }

      case EstimateStatus.unpaidWithoutResponse:
      case EstimateStatus.readyForPayment:
        return {
          label: <Badge color="orange">Ready for Payment</Badge>,
          bottomContent: (
            <div className="space-y-4">
              {federalTaxEstimate?.safeHarborType === SafeHarborType.ES ? (
                <WhatIsEs1040Popup />
              ) : (
                <WhatIsSafeHarborPopup />
              )}
              <Text>
                Need to edit estimate? Click{' '}
                <Link
                  href="/taxes/quarterly/calculate-safe-harbor"
                  variant="secondary"
                >
                  here
                </Link>
                .
              </Text>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="w-full" onClick={openFlyout}>
                  View & Pay
                </Button>
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={() => setEstimateModalOpen(true)}
                >
                  Update status
                </Button>
              </div>
            </div>
          ),
        }
      case EstimateStatus.paid: {
        const paidAt = federalTaxEstimate?.paidAt
          ? DateTime.fromISO(federalTaxEstimate.paidAt).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_LONG
            )
          : null

        return {
          label: <Badge color="green">Paid</Badge>,
          bottomContent: (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon
                    icon={solid('check')}
                    className="text-green-500 h-4 w-4"
                  />
                  {paidAt && (
                    <Text variant={TypographyStyles.BODY_SM}>
                      paid {paidAt}
                    </Text>
                  )}
                </div>
                {document?.signedUrl ? (
                  <Link newPage href={document.signedUrl}>
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={solid('check')}
                        className="text-green-500 h-4 w-4"
                      />
                      <Button variant="link" className="text-sm">
                        receipt uploaded
                      </Button>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center gap-2">
                    <Icon
                      icon={solid('xmark')}
                      className="text-red-500 h-4 w-4"
                    />
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      className="underline"
                    >
                      add missing receipt
                    </Text>
                  </div>
                )}
              </div>
              <Button onClick={() => setEstimateModalOpen(true)} variant="link">
                Update Payment
              </Button>
            </div>
          ),
        }
      }
      case EstimateStatus.upcoming:
      case EstimateStatus.awaitingEstimate:
      case EstimateStatus.noIncomeTax:
      case EstimateStatus.joinedTooLate:
        return null
      default:
        return status satisfies never
    }
  }, [status, federalTaxEstimate, document, openFlyout])

  if (!federalTaxEstimate) {
    return null
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="w-full lg:w-7/12">
        <Card color={CardColors.LEVEL_1} className="-mb-5">
          <div className="space-y-4">
            {conditionalContent?.label}

            <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
              federal
            </Text>

            <Text variant={TypographyStyles.DISPLAY_SM}>
              {formatCurrencyFromCents(federalTaxEstimate.estimateInCents, {
                hideDecimalsIfZero: true,
              })}
            </Text>

            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {federalTaxEstimate.safeHarborType === SafeHarborType.ES
                ? `Q${quarter} 1040-ES estimate`
                : 'Safe harbor estimate'}
            </Text>

            {conditionalContent?.bottomContent}
          </div>

          <UpdateQuarterlyTaxEstimateModal
            open={estimateModalOpen}
            close={() => setEstimateModalOpen(false)}
            estimate={federalTaxEstimate}
          />
        </Card>
      </div>
    </div>
  )
}
